/**
 * Запросы к `/api/public/analytics/*` (US-04.1…04.8) — типизированные
 * контракты под виджеты раздела «Аналитика» (as-is `09_analytics_api.md`).
 *
 * Параметры одинаковы для всех эндпоинтов: `kato` (обязательный) и `year`
 * (опциональный; без него бэк отдаёт последний доступный год — фактический
 * год приходит в поле `year` ответа).
 *
 * Все ответы прогоняются через нормализаторы: пустое тело, отсутствующие
 * поля или мусор вместо ожидаемых типов превращаются в безопасные значения
 * (`null` / `[]` / отфильтрованные элементы). `null`/`[]` = «данных нет»
 * (виджет скрывается), `0` — реальный ноль из БД. Сетевые/HTTP-ошибки
 * реджектятся — их обрабатывает вызывающий код.
 */
import i18n from '../i18n';
import { api } from './api';
import type {
  AgricultureAnalyticsResponse,
  AgricultureBlockHead,
  AgricultureCropAreaResponse,
  AgricultureCropYieldResponse,
  AgricultureCropYieldRow,
  AgricultureGrossRow,
  AgricultureHeadcountResponse,
  AgricultureIfo,
  AgricultureLivestockProductionResponse,
  AgricultureMeasure,
  AgriculturePeriod,
  AgricultureProducersResponse,
  AgricultureRelease,
  AgricultureSownAreaPart,
  AgricultureSownAreaRow,
  AgricultureSummaryResponse,
  AnalyticsAllResponse,
  BudgetExecution,
  BudgetAbpRow,
  BudgetExpenditureRow,
  BudgetIncomeRow,
  LegalTaxesCompany,
  LegalTaxesPayments,
  LegalTaxesResponse,
  PricesLaborResponse,
  RegionEconomyResponse,
  LegalTaxesSectionCompanies,
  ConstructionAnalyticsResponse,
  ConstructionEmergencyHousingResponse,
  ConstructionEmergencyMetric,
  ConstructionNonresidentialResponse,
  ConstructionNonresidentialRow,
  ConstructionNonresidentialTerritory,
  ConstructionPlashkaResponse,
  EconomyAnalyticsResponse,
  EconomyBlockHead,
  EconomyGroup,
  EconomyIndicator,
  EconomyPeriod,
  EconomySource,
  EconomyValue,
  InfrastructureAnalyticsResponse,
  LandFundAnalyticsResponse,
  PopulationAnalyticsResponse,
  RoadNetwork,
  SocialCategoryDetail,
  SocialEducationResponse,
  SocialEducationRow,
  SocialHealthcareResponse,
  SocialHealthcareRow,
  SocialAnalyticsResponse,
  SocialPlashkaResponse,
  StatIfo,
  StatMetric,
  StatPeriod,
  StatPeriodType,
  TradePlashkaResponse,
  TradePricesResponse,
  StatSource,
  TradeTerritoriesResponse,
  StatTrendDirection,
  StatValue,
  StatValueState,
  TradeWholesaleResponse,
} from '../types/analytics';
import type {
  SnpCount,
  SnpEducation,
  SnpHealthcare,
  SnpNeedsData,
} from '../types/snpNeeds';

// --- примитивы нормализации (как в public.request.ts) ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

/** Число из числа или числовой строки (КАТО «Мониторинга СНП» — строкой). */
const numLoose = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return null;
};

const bool = (v: unknown): boolean => v === true;

// --- нормализаторы ответов ---

export const normalizePopulationAnalytics = (
  raw: unknown,
): PopulationAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const movement = root.natural_movement == null ? null : asObj(root.natural_movement);
  const groups = root.social_groups == null ? null : asObj(root.social_groups);

  return {
    year: num(root.year),
    plashka: {
      population: num(plashka.population),
      economically_active_population: num(
        plashka.economically_active_population,
      ),
      youth: num(plashka.youth),
      unemployment_rate: num(plashka.unemployment_rate),
      unemployed: num(plashka.unemployed),
      saldo_migration: num(plashka.saldo_migration),
    },
    // Staging отдаёт дубли строк по году — берём первую запись.
    population_dynamics: (() => {
      const seen = new Set<number>();
      return asArr(root.population_dynamics)
        .flatMap((entry) => {
          const e = asObj(entry);
          const year = num(e.year);
          if (year === null || seen.has(year)) return [];
          seen.add(year);
          return [{ year, value: num(e.value), forecast: bool(e.forecast) }];
        })
        .sort((a, b) => a.year - b.year);
    })(),
    natural_movement: movement && {
      births: num(movement.births),
      deaths: num(movement.deaths),
      natural_increase: num(movement.natural_increase),
      immigration: num(movement.immigration),
      emigration: num(movement.emigration),
      saldo_migration: num(movement.saldo_migration),
    },
    social_groups: groups && {
      youth: num(groups.youth),
      hired: num(groups.hired),
      self_employed: num(groups.self_employed),
      unemployed: num(groups.unemployed),
      working_elderly: num(groups.working_elderly),
      tsa_recipients: num(groups.tsa_recipients),
      pensioners: num(groups.pensioners),
      large_families: num(groups.large_families),
      disabled: num(groups.disabled),
    },
    demography: asArr(root.demography).flatMap((entry) => {
      const e = asObj(entry);
      const age = str(e.age);
      if (age === null) return [];
      return [{ age, population: num(e.population) }];
    }),
    labor_market: asArr(root.labor_market)
      .flatMap((entry) => {
        const e = asObj(entry);
        const year = num(e.year);
        if (year === null) return [];
        return [
          {
            year,
            economically_active: num(e.economically_active),
            employed: num(e.employed),
            avg_salary: num(e.avg_salary),
          },
        ];
      })
      .sort((a, b) => a.year - b.year),
  };
};

const budgetExecutionFrom = (raw: unknown): BudgetExecution | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    dev_programs_count: num(e.dev_programs_count),
    cur_year_dev_budget: num(e.cur_year_dev_budget),
    prev_year_dev_budget: num(e.prev_year_dev_budget),
    active_programs_count: num(e.active_programs_count),
    cur_year_active_budget: num(e.cur_year_active_budget),
    prev_year_active_budget: num(e.prev_year_active_budget),
    total_programs_count: num(e.total_programs_count),
    total_cur_year_budget: num(e.total_cur_year_budget),
    total_prev_year_budget: num(e.total_prev_year_budget),
    dev_budget_processing: num(e.dev_budget_processing),
    active_budget_processing: num(e.active_budget_processing),
    total_budget_processing: num(e.total_budget_processing),
  };
};

export const normalizeEconomyAnalytics = (
  raw: unknown,
): EconomyAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const msbRaw = root.msb == null ? null : asObj(root.msb);
  const subjects =
    msbRaw && msbRaw.subjects != null ? asObj(msbRaw.subjects) : null;

  return {
    year: num(root.year),
    plashka: {
      avg_salary: num(plashka.avg_salary),
      investment_ifo: num(plashka.investment_ifo),
      investment_val: num(plashka.investment_val),
      budget_execution_pct: num(plashka.budget_execution_pct),
      tax_revenues_fact: num(plashka.tax_revenues_fact),
      industry_ifo: num(plashka.industry_ifo),
      industry_val: num(plashka.industry_val),
    },
    // Ответ может содержать дубли по subtype — берём первую запись.
    economic_development: (() => {
      const seen = new Set<string>();
      return asArr(root.economic_development).flatMap((entry) => {
        const e = asObj(entry);
        const subtype = str(e.subtype);
        if (subtype === null || seen.has(subtype)) return [];
        seen.add(subtype);
        return [
          {
            subtype,
            current_year_val: num(e.current_year_val),
            previous_year_val: num(e.previous_year_val),
            ifo: num(e.ifo),
            change_pct: num(e.change_pct),
            increase_val: num(e.increase_val),
            ifo_place: num(e.ifo_place),
          },
        ];
      });
    })(),
    budget_execution: budgetExecutionFrom(root.budget_execution),
    // «Освоение бюджета» / «Доходы» из analytics-БД (DP-134); вне СКО — [].
    budget_expenditure: asArr(root.budget_expenditure).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      const row: BudgetExpenditureRow = {
        name,
        plan_yearly: num(e.plan_yearly),
        plan_period: num(e.plan_period),
        fact_period: num(e.fact_period),
        execution_pct: num(e.execution_pct),
      };
      return [row];
    }),
    // Разрез по АБП (DP-483): строки без единого числа отбрасываем — пустая
    // строка сдвинула бы скролл, ничего не сообщая.
    budget_expenditure_abp: asArr(root.budget_expenditure_abp).flatMap(
      (entry) => {
        const e = asObj(entry);
        const row: BudgetAbpRow = {
          abp_code: str(e.abp_code),
          name_ru: str(e.name_ru),
          name_kz: str(e.name_kz),
          plan_yearly: num(e.plan_yearly),
          plan_period: num(e.plan_period),
          fact_period: num(e.fact_period),
          execution_pct: num(e.execution_pct),
          annual_utilization_pct: num(e.annual_utilization_pct),
        };
        const empty =
          row.plan_yearly === null &&
          row.plan_period === null &&
          row.fact_period === null &&
          row.execution_pct === null;
        return empty || (row.name_ru === null && row.name_kz === null)
          ? []
          : [row];
      },
    ),
    budget_expenditure_actual_date: str(root.budget_expenditure_actual_date),
    budget_income: asArr(root.budget_income).flatMap((entry) => {
      const e = asObj(entry);
      const ktkName = str(e.ktk_name);
      if (ktkName === null) return [];
      const row: BudgetIncomeRow = {
        ktk_name: ktkName,
        plan_yearly: num(e.plan_yearly),
        plan_period: num(e.plan_period),
        fact_period: num(e.fact_period),
        execution_pct: num(e.execution_pct),
      };
      return [row];
    }),
    income_tax: asArr(root.income_tax).map((entry) => {
      const e = asObj(entry);
      return {
        type: str(e.type),
        tax_revenues_fact: num(e.tax_revenues_fact),
        tax_revenues_plan: num(e.tax_revenues_plan),
        tax_revenues_annual: num(e.tax_revenues_annual),
        non_tax_revenues_fact: num(e.non_tax_revenues_fact),
        non_tax_revenues_plan: num(e.non_tax_revenues_plan),
        non_tax_revenues_annual: num(e.non_tax_revenues_annual),
        fixed_assets_sale_fact: num(e.fixed_assets_sale_fact),
        fixed_assets_sale_plan: num(e.fixed_assets_sale_plan),
        fixed_assets_sale_annual: num(e.fixed_assets_sale_annual),
      };
    }),
    msb: msbRaw && {
      subjects: subjects && {
        active_count: num(subjects.active_count),
        registered_count: num(subjects.registered_count),
      },
      activities: asArr(msbRaw.activities).flatMap((entry) => {
        const e = asObj(entry);
        const activity = str(e.activity);
        if (activity === null) return [];
        return [{ activity, count: num(e.count) }];
      }),
      financial: asArr(msbRaw.financial).flatMap((entry) => {
        const e = asObj(entry);
        const kind = str(e.kind);
        if (kind === null) return [];
        return [{ kind, projects: num(e.projects), amount: num(e.amount) }];
      }),
    },
  };
};

export const normalizeAgricultureAnalytics = (
  raw: unknown,
): AgricultureAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  return {
    year: num(root.year),
    plashka: {
      cattle_heads: num(plashka.cattle_heads),
      self_sufficiency_pct: num(plashka.self_sufficiency_pct),
      producers: num(plashka.producers),
      meat_total_t: num(plashka.meat_total_t),
      gross_harvest_ths_t: num(plashka.gross_harvest_ths_t),
    },
    sown_areas: asArr(root.sown_areas).flatMap((entry) => {
      const e = asObj(entry);
      const crop = str(e.crop);
      if (crop === null) return [];
      return [
        {
          crop,
          area_ths_ha: num(e.area_ths_ha),
          gross_harvest_ths_t: num(e.gross_harvest_ths_t),
          producers: num(e.producers),
        },
      ];
    }),
    sown_total: (() => {
      if (root.sown_total == null) return null;
      const e = asObj(root.sown_total);
      return {
        area_ths_ha: num(e.area_ths_ha),
        gross_harvest_ths_t: num(e.gross_harvest_ths_t),
      };
    })(),
    livestock_dynamics: asArr(root.livestock_dynamics)
      .flatMap((entry) => {
        const e = asObj(entry);
        const year = num(e.year);
        if (year === null) return [];
        return [
          {
            year,
            cattle: num(e.cattle),
            small_cattle: num(e.small_cattle),
            horses: num(e.horses),
            birds: num(e.birds),
            camels: num(e.camels),
          },
        ];
      })
      .sort((a, b) => a.year - b.year),
    livestock: asArr(root.livestock).flatMap((entry) => {
      const e = asObj(entry);
      const animal = str(e.animal);
      if (animal === null) return [];
      return [
        {
          animal,
          kh_too: num(e.kh_too),
          lph: num(e.lph),
          total: num(e.total),
          meat_direction: num(e.meat_direction),
          breeding: num(e.breeding),
          cows: num(e.cows),
        },
      ];
    }),
    poultry: asArr(root.poultry).flatMap((entry) => {
      const e = asObj(entry);
      const kind = str(e.kind);
      if (kind === null) return [];
      return [
        {
          kind,
          kh_too: num(e.kh_too),
          lph: num(e.lph),
          total: num(e.total),
        },
      ];
    }),
    poultry_total: num(root.poultry_total),
    meat_production: asArr(root.meat_production).flatMap((entry) => {
      const e = asObj(entry);
      const animal = str(e.animal);
      if (animal === null) return [];
      return [{ animal, tons: num(e.tons) }];
    }),
    dairy_production: asArr(root.dairy_production).flatMap((entry) => {
      const e = asObj(entry);
      const product = str(e.product);
      if (product === null) return [];
      return [
        {
          product,
          annual_output_t: num(e.annual_output_t),
          milk_consumption_ths_l: num(e.milk_consumption_ths_l),
        },
      ];
    }),
  };
};

export const normalizeLandFundAnalytics = (
  raw: unknown,
): LandFundAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const settlement = root.settlement == null ? null : asObj(root.settlement);
  const reserve = root.reserve == null ? null : asObj(root.reserve);
  return {
    year: num(root.year),
    plashka: {
      total_land_ha: num(plashka.total_land_ha),
      arable_ha: num(plashka.arable_ha),
      pasture_ha: num(plashka.pasture_ha),
      reserve_total_ha: num(plashka.reserve_total_ha),
    },
    settlement: settlement && {
      settlement_land_ha: num(settlement.settlement_land_ha),
      agricultural_total_ha: num(settlement.agricultural_total_ha),
      agricultural_arable_ha: num(settlement.agricultural_arable_ha),
      agricultural_pasture_ha: num(settlement.agricultural_pasture_ha),
    },
    reserve: reserve && {
      reserve_total_ha: num(reserve.reserve_total_ha),
      reserve_arable_ha: num(reserve.reserve_arable_ha),
      reserve_pasture_ha: num(reserve.reserve_pasture_ha),
      other_land_ha: num(reserve.other_land_ha),
    },
  };
};

const roadNetworkFrom = (raw: unknown): RoadNetwork | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    year: num(e.year),
    total_km: num(e.total_km),
    normative_pct: num(e.normative_pct),
    roads: asArr(e.roads).flatMap((entry) => {
      const road = asObj(entry);
      const key = str(road.key);
      if (key === null) return [];
      return [
        {
          key,
          name_ru: str(road.name_ru) ?? '',
          total_km: num(road.total_km),
          normative_km: num(road.normative_km),
          normative_pct: num(road.normative_pct),
          satisfactory_km: num(road.satisfactory_km),
          satisfactory_pct: num(road.satisfactory_pct),
          bad_km: num(road.bad_km),
          bad_pct: num(road.bad_pct),
        },
      ];
    }),
  };
};

export const normalizeInfrastructureAnalytics = (
  raw: unknown,
): InfrastructureAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const water = root.water_supply == null ? null : asObj(root.water_supply);
  return {
    year: num(root.year),
    plashka: {
      clean_water_pct: num(plashka.clean_water_pct),
      gas_coverage_pct: num(plashka.gas_coverage_pct),
      internet_coverage_pct: num(plashka.internet_coverage_pct),
      roads_normative_pct: num(plashka.roads_normative_pct),
      roads_total_km: num(plashka.roads_total_km),
    },
    engineering: asArr(root.engineering).flatMap((entry) => {
      const e = asObj(entry);
      const key = str(e.key);
      if (key === null) return [];
      return [
        {
          key,
          snp_coverage_pct: num(e.snp_coverage_pct),
          population_coverage_pct: num(e.population_coverage_pct),
          growth_pct: num(e.growth_pct),
          // Наличие по СНП (мониторинг СНП, лист 5) и чипы операторов.
          covered_snp: num(e.covered_snp),
          total_snp: num(e.total_snp),
          operators:
            e.operators == null
              ? null
              : asArr(e.operators).flatMap((operatorEntry) => {
                  const operator = asObj(operatorEntry);
                  const name = str(operator.name);
                  if (name === null) return [];
                  return [{ name, covered_snp: num(operator.covered_snp) }];
                }),
        },
      ];
    }),
    water_supply: water && {
      network_length_km: num(water.network_length_km),
      clean_water_access_pct: num(water.clean_water_access_pct),
      centralized_water_pct: num(water.centralized_water_pct),
      decentralized_water_pct: num(water.decentralized_water_pct),
      trucked_water_population: num(water.trucked_water_population),
      no_access_population: num(water.no_access_population),
    },
    roads: roadNetworkFrom(root.roads),
  };
};

/**
 * «Потребности СНП» — облегчённая версия (US-04.9.1), структура по
 * прототипу `snp-passport-district (1).html`. Эндпоинт
 * `/public/analytics/snp-needs` на backend ещё НЕ реализован: контракт
 * заложен заранее, до появления реста запрос отклоняется 404 — секция
 * показывает заголовки блоков с подписью «Нет данных», без ошибки.
 */
/**
 * `/analytics/snp-needs` (US-04.9.1) — «Мониторинг СНП», агрегация до
 * района/области считается на бэке.
 *
 * Ключевая особенность контракта: почти каждый показатель приходит парой
 * `{present, answered}` — «сколько сёл под условием» и «сколько сёл вообще
 * ответило по этому показателю». Графы «Мониторинга» заполнены неравномерно
 * (автобусное сообщение — 45 сёл из 585, медпункт — 512), поэтому доли и
 * подписи «N из M» считаются от `answered` показателя, а не от общего числа
 * сёл. `null` вместо объекта = данных по теме нет вовсе.
 *
 * Ключи ответа переводятся в ключи словаря i18n (`topics.*`,
 * `healthcare.facilities.*`), незнакомые — отбрасываются.
 */

/** Ключ отклонения в ответе → ключ подписи `analytics.snpNeeds.topics.*`. */
const SNP_DEVIATION_KEYS: Record<string, string> = {
  lep_wear_over_75: 'linesWear',
  tp_wear_over_threshold: 'substationsWear',
  road_condition_poor: 'innerRoads',
  no_bus: 'busService',
  no_mobile_internet: 'internet',
  doctors_deficit: 'doctorsDeficit',
  nurses_deficit: 'nursesDeficit',
  centralized_water_low: 'centralWater',
  delivered_water_population: 'deliveredWater',
  school_places_needed: 'schoolPlaces',
  kindergarten_places_needed: 'kindergartenPlaces',
};

/** Тип медучреждения в ответе → ключ подписи `healthcare.facilities.*`. */
const SNP_FACILITY_KEYS: Record<string, string> = {
  crb: 'crh',
  district_hospital: 'rb',
  rural_hospital: 'sb',
  polyclinic: 'policlinic',
  outpatient_clinic: 'ambulatory',
  fap: 'fap',
  medical_point: 'medpoint',
};

/** Бакет износа в ответе → ключ подписи `engineering.electricity.*`. */
const SNP_WEAR_KEYS: Array<[string, string]> = [
  ['under_50_pct', 'under50'],
  ['between_50_75_pct', 'mid'],
  ['over_75_pct', 'over75'],
];

/** Пара «под условием / ответило»; нет объекта → null. */
const snpCountFrom = (raw: unknown): SnpCount | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return { present: num(e.present), answered: num(e.answered) };
};

/** «Обратная» пара: сёла, НЕ попавшие под условие (нет связи, плохая вода). */
const snpCountInverted = (raw: unknown): SnpCount | null => {
  const pair = snpCountFrom(raw);
  if (pair === null) return null;
  const missing =
    pair.present !== null && pair.answered !== null
      ? pair.answered - pair.present
      : null;
  return { present: missing, answered: pair.answered };
};

/**
 * Бакеты износа `{under_50_pct, between_50_75_pct, over_75_pct, total}` —
 * это счётчики СЁЛ, а не проценты; доли считаем от `total`.
 */
const snpWearFrom = (
  raw: unknown,
): Array<{ key: string; villages: number | null; pct: number | null }> => {
  if (raw == null) return [];
  const e = asObj(raw);
  const total = num(e.total);
  return SNP_WEAR_KEYS.flatMap(([field, key]) => {
    const villages = num(e[field]);
    if (villages === null) return [];
    return [
      {
        key,
        villages,
        pct: total !== null && total > 0 ? (villages / total) * 100 : null,
      },
    ];
  });
};

export const normalizeSnpNeeds = (raw: unknown): SnpNeedsData => {
  const root = asObj(raw);

  const water = root.water == null ? null : asObj(root.water);
  const engineering =
    root.engineering == null ? null : asObj(root.engineering);
  const coverage =
    engineering && engineering.coverage != null
      ? asObj(engineering.coverage)
      : null;
  const roads = root.roads == null ? null : asObj(root.roads);
  const healthcare = root.healthcare == null ? null : asObj(root.healthcare);
  const facilities =
    healthcare && healthcare.facilities != null
      ? asObj(healthcare.facilities)
      : null;
  const staff =
    healthcare && healthcare.staff != null ? asObj(healthcare.staff) : null;
  const deviations =
    root.deviations == null ? null : asObj(root.deviations);
  const distribution =
    root.deviation_distribution == null
      ? null
      : asObj(root.deviation_distribution);

  // Население — сумма по источникам воды: тот же знаменатель, от которого
  // бэк считает centralized_level_pct / decentralized_level_pct.
  const waterPeople = water
    ? [
        num(water.centralized_population),
        num(water.decentralized_population),
        num(water.delivered_population),
      ]
    : [];
  const population = waterPeople.some((value) => value !== null)
    ? waterPeople.reduce<number>((acc, value) => acc + (value ?? 0), 0)
    : null;

  const waterSource = (
    key: string,
    people: number | null,
    pct: number | null,
  ): Array<{ key: string; people: number | null; pct: number | null }> =>
    people === null && pct === null
      ? []
      : [
          {
            key,
            people,
            pct:
              pct ??
              (people !== null && population !== null && population > 0
                ? (people / population) * 100
                : null),
          },
        ];

  // «Образование»: дефициты приходят готовыми (deficit ≤ 0 — профицит),
  // подвоз — парой school_busing «N из M ответивших».
  const education = root.education == null ? null : asObj(root.education);
  const educationData: SnpEducation | null = education && {
    kindergartenCapacity: num(education.kindergarten_capacity),
    kindergartenDeficit: num(education.kindergarten_deficit),
    schoolCapacity: num(education.school_capacity),
    schoolDeficit: num(education.school_deficit),
    schoolDistanceKm: num(education.school_distance_km),
    busingOrganized: null,
    busing: snpCountFrom(education.school_busing),
  };

  const healthcareData: SnpHealthcare | null = healthcare && {
    // Эндпоинт «сёл без единого учреждения» не отдаёт (US-03 / бэк).
    villagesNoFacility: null,
    facilities: Object.entries(SNP_FACILITY_KEYS).flatMap(
      ([field, key]) => {
        const count = snpCountFrom(facilities?.[field]);
        return count === null ? [] : [{ key, count }];
      },
    ),
    beds: num(staff?.hospital_beds),
    pharmacies: num(staff?.pharmacies),
    totalFacilities: num(staff?.total_facilities),
    staff: staff && {
      doctorsStaffed: num(staff.doctors_staffed),
      doctorsActual: num(staff.doctors_actual),
      doctorsDeficitPct: num(staff.doctors_deficit_pct),
      nursesStaffed: num(staff.nurses_staffed),
      nursesActual: num(staff.nurses_actual),
      nursesDeficitPct: num(staff.nurses_deficit_pct),
    },
  };

  // Состояние дорог приходит только в км — доли считаем от суммы известных
  // состояний (village_roads_total_km в «Мониторинге» с ней не сходится).
  const conditionDefs: Array<[string, number | null]> = roads
    ? [
        ['good', num(roads.condition_good_km)],
        ['satisfactory', num(roads.condition_fair_km)],
        ['bad', num(roads.condition_poor_km)],
      ]
    : [];
  const conditionSum = conditionDefs.reduce(
    (acc, [, km]) => acc + (km ?? 0),
    0,
  );

  const pavedKm = roads ? num(roads.village_roads_paved_km) : null;
  const roadsTotalKm = roads ? num(roads.village_roads_total_km) : null;

  return {
    year: num(root.year),
    totalVillages: num(root.settlement_count),
    population,
    // Темы отклонений — фиксированный список ключей, по убыванию.
    themes: deviations
      ? Object.entries(SNP_DEVIATION_KEYS)
          .flatMap(([field, key]) => {
            const pair = snpCountFrom(deviations[field]);
            if (pair === null || pair.present === null) return [];
            return [
              { key, villages: pair.present, answered: pair.answered },
            ];
          })
          .sort((a, b) => (b.villages ?? 0) - (a.villages ?? 0))
      : [],
    // Список сёл бэк отдаёт уже отсортированным (deviation_count desc,
    // при равенстве — code_kato).
    villages: asArr(root.top_settlements_by_deviations).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      const kato = numLoose(e.code_kato);
      return [
        {
          kato,
          name,
          okrug: str(e.okrug),
          population: num(e.population),
          // Перечень тем по селу эндпоинт не отдаёт — только количество.
          deviations: [],
          deviationCount: num(e.deviation_count),
          hasData: kato !== null,
        },
      ];
    }),
    distribution: distribution && {
      none: num(distribution.none),
      low: num(distribution.one_to_two),
      mid: num(distribution.three_to_four),
      high: num(distribution.five_plus),
    },
    engineering: {
      water: water && {
        sources: [
          ...waterSource(
            'central',
            num(water.centralized_population),
            num(water.centralized_level_pct),
          ),
          ...waterSource(
            'decentral',
            num(water.decentralized_population),
            num(water.decentralized_level_pct),
          ),
          ...waterSource('delivered', num(water.delivered_population), null),
        ],
        delivered: snpCountFrom(deviations?.delivered_water_population),
        // quality_compliant — сёла с водой В НОРМЕ; показываем обратное.
        badQuality: snpCountInverted(water.quality_compliant),
      },
      electricity: engineering && {
        wearDistribution: snpWearFrom(engineering.lep_wear_buckets),
        tpWearDistribution: snpWearFrom(engineering.tp_wear_buckets),
        avgWearPct: num(engineering.lep_wear_pct),
        linesKm: num(engineering.lep_length_km),
        substations: num(engineering.tp_count),
        centralized: snpCountFrom(coverage?.centralized_electricity_coverage),
      },
      gas: engineering && {
        housesPct: num(engineering.gasified_houses_pct),
        natural: snpCountFrom(coverage?.gas_supply_coverage),
        lpg: snpCountFrom(coverage?.liquefied_gas_coverage),
        gasifiedPopulation: num(engineering.gasified_population),
      },
      comms: coverage && {
        mobile: snpCountFrom(coverage.cellular_coverage),
        mobileInternet: snpCountFrom(coverage.mobile_internet_coverage),
        broadband: snpCountFrom(coverage.broadband_coverage),
        publicBroadband: snpCountFrom(
          coverage.public_institution_broadband_coverage,
        ),
      },
    },
    roads: roads && {
      totalKm: roadsTotalKm,
      condition: conditionDefs.flatMap(([key, km]) =>
        km === null
          ? []
          : [
              {
                key,
                km,
                pct: conditionSum > 0 ? (km / conditionSum) * 100 : null,
              },
            ],
      ),
      surface: {
        hardKm: pavedKm,
        hardPct:
          pavedKm !== null && roadsTotalKm !== null && roadsTotalKm > 0
            ? (pavedKm / roadsTotalKm) * 100
            : null,
        // «Без покрытия» = всего минус с твёрдым покрытием: surface_dirt_km
        // «Мониторинга» с общей протяжённостью не сходится (по СКО 4 368,9
        // км грунтовых при 4 309,6 км всех дорог), поэтому берём разность,
        // а к грунтовке падаем только когда покрытых дорог нет в ответе.
        noneKm:
          pavedKm !== null && roadsTotalKm !== null
            ? Math.max(0, roadsTotalKm - pavedKm)
            : num(roads.surface_dirt_km),
        capitalRepairKm: num(roads.capital_repair_needed_km),
        mediumRepairKm: num(roads.medium_repair_needed_km),
      },
      transport: {
        bus: snpCountFrom(roads.bus_service),
        offroadKm: null,
        offroadVillages: null,
        routesTotalKm: null,
      },
    },
    education: educationData,
    healthcare: healthcareData,
  };
};

/**
 * `/analytics/all` (US-04.1) — все разделы одним ответом: каждый слайс
 * прогоняется своим нормализатором. Слайса нет (или он `null`) → `null`:
 * секция понимает, что раздел из общего ответа не пришёл, и грузит свой
 * эндпоинт сама (сейчас так с `snp_needs`).
 */
export const normalizeAnalyticsAll = (
  raw: unknown,
): AnalyticsAllResponse => {
  const root = asObj(raw);
  const slice = <T>(
    value: unknown,
    normalize: (raw: unknown) => T,
  ): T | null =>
    typeof value === 'object' && value !== null ? normalize(value) : null;

  return {
    year: num(root.year),
    population: slice(root.population, normalizePopulationAnalytics),
    economy: slice(root.economy, normalizeEconomyAnalytics),
    agriculture: slice(root.agriculture, normalizeAgricultureAnalytics),
    landFund: slice(root.land_fund, normalizeLandFundAnalytics),
    infrastructure: slice(
      root.infrastructure,
      normalizeInfrastructureAnalytics,
    ),
    snpNeeds: slice(root.snp_needs, normalizeSnpNeeds),
  };
};

/**
 * «Налоговые поступления юридических лиц» (форма 2-47, FR-3a) — три
 * эндпоинта `/analytics/legal-entity-taxes*`. Ключ территории — `kato`:
 * бэк сам резолвит его в top_code_region через kato_convert (закрытый
 * открытый вопрос тикета); КАТО без соответствия → 404 с detail.
 *
 * Суммы — тенге (amount_unit: "KZT"); период фактических данных приходит
 * в ответе (period) — фронт его только показывает, дат не передаёт.
 * total === null — данных по территории нет (карточки показывают
 * «Нет данных»; покрывает и 200 с пустым каркасом, и 404 через catch).
 *
 * Таймаут — свой (20 с вместо общих 10 с клиента): основной эндпоинт
 * без дат отвечает ~6 с даже на свежем бэке.
 */
const LEGAL_TAXES_TIMEOUT_MS = 20000;
const legalTaxesCompaniesFrom = (raw: unknown): LegalTaxesCompany[] =>
  asArr(raw).flatMap((entry) => {
    const e = asObj(entry);
    const name = str(e.name);
    if (name === null) return [];
    return [
      {
        bin: str(e.bin),
        name,
        oked: str(e.oked),
        oked_name: str(e.oked_name),
        section: str(e.section),
        amount: num(e.amount),
      },
    ];
  });

export const normalizeLegalTaxes = (raw: unknown): LegalTaxesResponse => {
  const root = asObj(raw);
  const period = root.period == null ? null : asObj(root.period);
  return {
    period: period && {
      date_from: str(period.date_from),
      date_to: str(period.date_to),
    },
    total: num(root.total),
    // Единица сумм объявлена ответом (US-36.1 FR-4.4) — экран её не
    // выводит из величины и сам ничего не делит.
    amount_unit_ru: str(root.amount_unit_ru),
    amount_unit_kz: str(root.amount_unit_kz),
    top: legalTaxesCompaniesFrom(root.top),
    by_activity: asArr(root.by_activity).flatMap((entry) => {
      const e = asObj(entry);
      const section = str(e.section);
      if (section === null) return [];
      return [{ section, amount: num(e.amount), share: num(e.share) }];
    }),
  };
};

export const normalizeLegalTaxesSection = (
  raw: unknown,
): LegalTaxesSectionCompanies => {
  const root = asObj(raw);
  return {
    total: num(root.total),
    items: legalTaxesCompaniesFrom(root.items),
  };
};

/** `2026` | «2026-06-24» → «2026»; мусор → null (US-36.1 FR-4.4). */
const paidYear = (v: unknown): string | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  const s = str(v);
  if (s === null) return null;
  const match = /^(\d{4})/.exec(s);
  return match ? match[1] : s;
};

export const normalizeLegalTaxesPayments = (
  raw: unknown,
): LegalTaxesPayments => {
  const root = asObj(raw);
  return {
    total: num(root.total),
    items: asArr(root.items).map((entry) => {
      const e = asObj(entry);
      const amount = num(e.amount);
      // Год оплаты приходит целым числом (US-36.1 FR-4.4); прежняя
      // строка-дата тоже приводится к году — переход выкатывается
      // вместе с экраном, но ответ из кэша ломать вёрстку не должен.
      const paidAt = paidYear(e.paid_at);
      // DP-520: строка — группа «КБК × год». Ответ бэка до DP-520 платежей
      // внутри не несёт: строка тогда и есть единственный платёж группы.
      const payments = Array.isArray(e.payments)
        ? asArr(e.payments).map((payment) => {
            const p = asObj(payment);
            return { amount: num(p.amount), paid_at: paidYear(p.paid_at) };
          })
        : [{ amount, paid_at: paidAt }];
      return {
        kbk: str(e.kbk),
        kbk_name: str(e.kbk_name),
        amount,
        paid_at: paidAt,
        payments_count: num(e.payments_count) ?? payments.length,
        payments,
      };
    }),
  };
};

/** Обе диаграммы виджета одним ответом. */
export const getLegalEntityTaxes = async (
  kato: number,
): Promise<LegalTaxesResponse> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes',
    { params: { kato }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxes(response.data);
};

/** Drill-through: ВСЕ компании раздела ОКЭД (section — как в by_activity). */
export const getLegalTaxesByActivity = async (
  kato: number,
  section: string,
): Promise<LegalTaxesSectionCompanies> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes/by-activity',
    { params: { kato, section }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxesSection(response.data);
};

/** Раскрытие строки компании: платежи по БИН (КБК · сумма · дата). */
export const getLegalTaxesPayments = async (
  kato: number,
  bin: string,
): Promise<LegalTaxesPayments> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes/payments',
    { params: { kato, bin }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxesPayments(response.data);
};

// --- /analytics/trade/* (US-30 «Торговля», контракт — US-31) ---

/**
 * Состояния значения контракта (US-31 FR-6). Неизвестное состояние не
 * выдумывается: значение есть → «value», нет → «no_data».
 */
const STAT_STATES = [
  'value',
  'absent',
  'confidential',
  'not_published',
  'negligible',
  'no_data',
  'not_applicable',
] as const;

const statState = (raw: unknown, hasValue: boolean): StatValueState =>
  typeof raw === 'string' && (STAT_STATES as readonly string[]).includes(raw)
    ? (raw as StatValueState)
    : hasValue
      ? 'value'
      : 'no_data';

const statSource = (raw: unknown): StatSource | null => {
  const src = asObj(raw);
  const source: StatSource = {
    name: str(src.name),
    issue: str(src.issue),
    published_at: str(src.published_at),
  };
  return source.name === null &&
    source.issue === null &&
    source.published_at === null
    ? null
    : source;
};

/**
 * Величина ответа: объект контракта (значение + состояние + единица +
 * признак расчётной) либо голое число — тогда состояние выводится из
 * наличия значения, а единица остаётся неизвестной (экран её не
 * подставляет, US-30 FR-7.1).
 */
const statValue = (raw: unknown): StatValue | null => {
  if (raw === null || raw === undefined) return null;
  const obj = typeof raw === 'number' ? { value: raw } : asObj(raw);
  const value = num(obj.value);
  return {
    value,
    state: statState(obj.state, value !== null),
    unit: str(obj.unit),
    calculated: bool(obj.calculated),
    formula: str(obj.formula),
    source: statSource(obj.source),
  };
};

const STAT_DIRECTIONS = ['up_is_good', 'up_is_bad'] as const;

/** ИФО — только из ответа (US-30 FR-8.3), экран его не вычисляет. */
const statIfo = (raw: unknown): StatIfo | null => {
  if (raw === null || raw === undefined) return null;
  const obj = typeof raw === 'number' ? { value: raw } : asObj(raw);
  const value = num(obj.value);
  return {
    value,
    state: statState(obj.state, value !== null),
    base_label: str(obj.base_label),
    direction:
      typeof obj.direction === 'string' &&
      (STAT_DIRECTIONS as readonly string[]).includes(obj.direction)
        ? (obj.direction as StatTrendDirection)
        : null,
    source: statSource(obj.source),
  };
};

// Типы отчётного показателя контракта: накопленный период, месяц,
// квартал, год и дата состояния (US-24 FR-2.1, US-18 FR-2.1).
const STAT_PERIOD_TYPES = [
  'cumulative',
  'month',
  'quarter',
  'year',
  'date',
] as const;

const statPeriod = (raw: unknown): StatPeriod | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  return {
    type:
      typeof obj.type === 'string' &&
      (STAT_PERIOD_TYPES as readonly string[]).includes(obj.type)
        ? (obj.type as StatPeriodType)
        : null,
    label: str(obj.label),
    lagging: bool(obj.lagging),
  };
};

const statMetric = (raw: unknown): StatMetric | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const value = statValue(obj.value);
  const ifo = statIfo(obj.ifo);
  return value === null && ifo === null ? null : { value, ifo };
};

const strList = (raw: unknown): string[] =>
  asArr(raw).flatMap((item) => {
    const name = str(item);
    return name === null ? [] : [name];
  });

export const normalizeTradePlashka = (raw: unknown): TradePlashkaResponse => {
  const root = asObj(raw);
  const month = asObj(root.month);
  const share = asObj(root.center_share);

  return {
    period: statPeriod(root.period),
    total: statMetric(root.total),
    retail: statMetric(root.retail),
    wholesale: statMetric(root.wholesale),
    month:
      root.month == null
        ? null
        : {
            period: statPeriod(month.period),
            value: statValue(month.value),
            retail: statValue(month.retail),
            wholesale: statValue(month.wholesale),
          },
    // Доля областного центра приходит только для уровня области
    // (US-31 FR-7.4) — на районе и городе плитки просто нет.
    center_share:
      root.center_share == null
        ? null
        : {
            territory: str(share.territory),
            total: statValue(share.total),
            retail: statValue(share.retail),
            wholesale: statValue(share.wholesale),
          },
  };
};

export const normalizeTradePrices = (raw: unknown): TradePricesResponse => {
  const root = asObj(raw);

  return {
    period: statPeriod(root.period),
    // Неприменимость территории объявляется явно (US-31 FR-1.4);
    // отсутствие признака = блок применим.
    applicable: root.applicable !== false,
    calculated: bool(root.calculated),
    formula: str(root.formula),
    averaged_territories: strList(root.averaged_territories),
    items: asArr(root.items).map((item) => {
      const row = asObj(item);
      return {
        code: str(row.code),
        name: str(row.name),
        unit: str(row.unit),
        group: str(row.group),
        price: statValue(row.price),
        details: asArr(row.details).map((detail) => {
          const primary = asObj(detail);
          return {
            territory: str(primary.territory),
            price: statValue(primary.price),
          };
        }),
      };
    }),
    source: statSource(root.source),
  };
};

export const normalizeTradeWholesale = (
  raw: unknown,
): TradeWholesaleResponse => {
  const root = asObj(raw);

  return {
    period: statPeriod(root.period),
    // Итог — из строки «Всего» источника, сложением групп не собирается
    // (US-31 FR-9.2).
    total: statValue(root.total),
    groups: asArr(root.groups).map((group) => {
      const row = asObj(group);
      return {
        code: str(row.code),
        name: str(row.name),
        turnover: statValue(row.turnover),
        share: statValue(row.share),
        details: asArr(row.details).map((detail) => {
          const primary = asObj(detail);
          return {
            code: str(primary.code),
            name: str(primary.name),
            turnover: statValue(primary.turnover),
          };
        }),
      };
    }),
    source: statSource(root.source),
  };
};

export const normalizeTradeTerritories = (
  raw: unknown,
): TradeTerritoriesResponse => {
  const root = asObj(raw);

  return {
    period: statPeriod(root.period),
    // Порядок строк задаёт ответ — экран не сортирует (US-31 FR-10.3).
    rows: asArr(root.rows).map((item) => {
      const row = asObj(item);
      return {
        kato: numLoose(row.kato),
        name: str(row.name),
        current: bool(row.current),
        retail: statMetric(row.retail),
        wholesale: statMetric(row.wholesale),
        total: statValue(row.total),
        share: statValue(row.share),
      };
    }),
    source: statSource(root.source),
  };
};

// --- /analytics/construction (US-23 «Строительство», контракт — US-24) ---
//
// Операция одна на весь подраздел: раздельных `construction/plashka`,
// `construction/nonresidential`, `construction/emergency-housing`
// (предложение US-24 FR-1.1) на стендах нет — они отвечают 404, тогда
// как `/public/analytics/construction` существует. Разбор приводит
// единый ответ к трём прежним моделям, и разная периодичность выпусков
// от этого не теряется: подпись у плашки и у каждого блока остаётся
// своей, из своей части ответа (US-23 FR-5.1 … FR-5.4).
//
// Ответ разбирается терпимо к обеим формам контракта, потому что он
// менялся вместе с составом виджетов:
//  - величины плашки лежат в `plashka` либо в корне; числом либо
//    объектом-величиной (US-24) — `statValue` понимает оба;
//  - блоки — либо массив строк, либо объект `{ period, rows, source }`;
//  - блок ввода объектов приходит как `nonresidential` либо
//    `nonresidential_objects`, аварийное жильё — как `emergency_housing`
//    либо `emergency`;
//  - источник блока берётся из блока, а если его там нет — общий из
//    корня ответа.
// Чего в ответе нет, то остаётся величиной без данных: состав ответа не
// домысливается.

/** Период части ответа: свои реквизиты, иначе отчётный год ответа. */
const constructionPeriod = (
  raw: unknown,
  year: number | null,
): StatPeriod | null => {
  const period = statPeriod(raw);
  if (period !== null) return period;
  return year === null
    ? null
    : { type: 'year', label: String(year), lagging: false };
};

/** Блок ответа: массив строк либо объект `{ period, rows, source }`. */
const constructionBlock = (
  raw: unknown,
): { period: unknown; rows: unknown[]; source: unknown } =>
  Array.isArray(raw)
    ? { period: null, rows: raw, source: null }
    : (() => {
        const obj = asObj(raw);
        return { period: obj.period, rows: asArr(obj.rows), source: obj.source };
      })();

export const normalizeConstructionPlashka = (
  raw: unknown,
  year: number | null = null,
): ConstructionPlashkaResponse => {
  const root = asObj(raw);
  // Величина ищется в `plashka`, а если её там нет — в корне ответа.
  const plashka = asObj(root.plashka);
  const pick = (key: string): unknown => plashka[key] ?? root[key];
  // `nonresidential` в корне может оказаться блоком таблицы, а не итогом
  // плашки: тогда ни `count`, ни `kinds_count` в нём нет и итог остаётся
  // пустым — число видов подставит сам экран по строкам блока (FR-2.5).
  const nonresidential = asObj(pick('nonresidential'));
  const count = statValue(nonresidential.count);
  // Число видов — для подписи «по N видам объектов» (US-23 FR-2.5);
  // константы «12 видов» из DP-384 экран не подставляет.
  const kindsCount = num(nonresidential.kinds_count);

  return {
    period: constructionPeriod(pick('period'), year),
    volume: statMetric(pick('volume')),
    housing: statMetric(pick('housing')),
    apartments: statValue(pick('apartments')),
    nonresidential:
      count === null && kindsCount === null
        ? null
        : { count, kinds_count: kindsCount },
  };
};

const constructionTerritory = (
  raw: unknown,
): ConstructionNonresidentialTerritory => {
  const row = asObj(raw);
  return {
    kato: numLoose(row.kato),
    name: str(row.name),
    count: statValue(row.count),
    actual: statValue(row.actual),
  };
};

export const normalizeConstructionNonresidential = (
  raw: unknown,
  year: number | null = null,
): ConstructionNonresidentialResponse => {
  const block = constructionBlock(raw);

  return {
    period: constructionPeriod(block.period, year),
    // Порядок строк задаёт ответ — экран не сортирует (US-24 FR-7.2);
    // строка без наименования вида объекта показать нечего.
    rows: block.rows.flatMap<ConstructionNonresidentialRow>((item) => {
      const row = asObj(item);
      const name = str(row.name);
      if (name === null) return [];
      return [
        {
          code: str(row.code),
          name,
          full_name: str(row.full_name),
          count: statValue(row.count),
          actual: statValue(row.actual),
          territories: asArr(row.territories).map(constructionTerritory),
        },
      ];
    }),
    source: statSource(block.source),
  };
};

/**
 * Величина блока аварийного жилья: ИФО источник не публикует (US-23
 * FR-4.2), поэтому динамика — изменение к предыдущему выпуску с
 * направлением из ответа. Нет изменения — плитка нейтральна, «рост»
 * экран сам не выводит.
 */
const constructionEmergencyMetric = (
  raw: unknown,
): ConstructionEmergencyMetric | null => {
  if (raw === null || raw === undefined) return null;
  const obj = typeof raw === 'number' ? { value: raw } : asObj(raw);
  const value = statValue(obj.value ?? raw);
  const change = statIfo(obj.change);
  return value === null && change === null ? null : { value, change };
};

export const normalizeConstructionEmergencyHousing = (
  raw: unknown,
  year: number | null = null,
): ConstructionEmergencyHousingResponse => {
  const root = asObj(raw);

  return {
    period: constructionPeriod(root.period, year),
    area: constructionEmergencyMetric(root.area),
    houses: constructionEmergencyMetric(root.houses),
    residents: constructionEmergencyMetric(root.residents),
    source: statSource(root.source),
  };
};

/** Весь подраздел одним ответом — то, что реально отдаёт бэк. */
export const normalizeConstruction = (
  raw: unknown,
): ConstructionAnalyticsResponse => {
  const root = asObj(raw);
  const year = num(root.year);
  // Общий источник корня — запасной для блока, который своего не прислал.
  const fallbackSource = statSource(root.source);
  const nonresidential = normalizeConstructionNonresidential(
    root.nonresidential_objects ?? root.nonresidential,
    year,
  );
  const emergencyHousing = normalizeConstructionEmergencyHousing(
    root.emergency_housing ?? root.emergency,
    year,
  );

  return {
    year,
    plashka: normalizeConstructionPlashka(root, year),
    nonresidential:
      nonresidential.source === null
        ? { ...nonresidential, source: fallbackSource }
        : nonresidential,
    emergency_housing:
      emergencyHousing.source === null
        ? { ...emergencyHousing, source: fallbackSource }
        : emergencyHousing,
  };
};

// --- US-24: раздельные операции «Строительства» ---
//
// Бэк отдаёт подраздел тремя операциями (`construction/summary`,
// `construction/nonresidential`, `construction/emergency-housing`) плюс
// разрез по территориям внутри вида объекта. Их ответы приводятся к тем
// же трём моделям, что и прежний единый ответ, — экран от этого не
// зависит. Наименования и подписи приходят парой ru/kz и выбираются по
// языку интерфейса на момент запроса.

const us24Kz = (): boolean => i18n.language === 'kz';

/** Поле пары `<base>_ru` / `<base>_kz` на языке интерфейса. */
const us24Text = (obj: Record<string, unknown>, base: string): string | null =>
  (us24Kz() ? str(obj[`${base}_kz`]) : null) ?? str(obj[`${base}_ru`]);

const US24_PERIOD_TYPES: Record<string, StatPeriodType> = {
  cumulative: 'cumulative',
  quarterly: 'quarter',
  on_date: 'date',
};

const us24Period = (raw: unknown): StatPeriod | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const type = str(obj.report_type);
  return {
    type: type !== null ? (US24_PERIOD_TYPES[type] ?? null) : null,
    label: us24Text(obj, 'label'),
    lagging: bool(obj.is_lagging),
  };
};

const us24Source = (raw: unknown): StatSource | null =>
  statSource({
    name: us24Text(asObj(raw), 'name'),
    published_at: asObj(raw).published_at,
  });

/**
 * Состояние величины US-24. `zero` — настоящий нуль, на экране это
 * обычное значение; `absent` объединяет прочерк и отсутствие строки, и
 * плитка показывает его как «нет данных».
 */
const us24State = (raw: unknown, hasValue: boolean): StatValueState => {
  if (raw === 'zero') return 'value';
  if (raw === 'absent') return 'no_data';
  return statState(raw, hasValue);
};

/**
 * Объём работ источник публикует в тенге: одиннадцатизначное число в
 * плитке не читается. Значение и объявленная единица пересчитываются
 * вместе, поэтому единица на экране остаётся верной.
 */
const us24Scale = (
  value: number | null,
  unit: string | null,
): { value: number | null; unit: string | null } =>
  unit === 'тенге' || unit === 'теңге'
    ? {
        value: value === null ? null : value / 1e9,
        unit: unit === 'тенге' ? 'млрд тенге' : 'млрд теңге',
      }
    : { value, unit };

/** Величина `MeasureOut`; уровень не обслуживается — `not_applicable`. */
const us24Value = (raw: unknown, levelSupported = true): StatValue | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const scaled = us24Scale(
    num(obj.value),
    us24Text(obj, 'unit') ?? str(obj.unit),
  );
  return {
    value: scaled.value,
    state: levelSupported
      ? us24State(obj.state, scaled.value !== null)
      : 'not_applicable',
    unit: scaled.unit,
    calculated: false,
    formula: null,
    source: us24Source(obj.source),
  };
};

const us24Ifo = (raw: unknown): StatIfo | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const value = num(obj.value);
  return {
    value,
    state: us24State(obj.state, value !== null),
    base_label: us24Text(obj, 'comparison_base'),
    direction:
      typeof obj.direction === 'string' &&
      (STAT_DIRECTIONS as readonly string[]).includes(obj.direction)
        ? (obj.direction as StatTrendDirection)
        : null,
    source: us24Source(obj.source),
  };
};

const us24Metric = (
  value: unknown,
  ifo: unknown,
  levelSupported: boolean,
): StatMetric | null => {
  const v = us24Value(value, levelSupported);
  const i = levelSupported ? us24Ifo(ifo) : null;
  return v === null && i === null ? null : { value: v, ifo: i };
};

export const normalizeConstructionSummary = (
  raw: unknown,
): ConstructionPlashkaResponse => {
  const root = asObj(raw);
  const supported = root.level_supported !== false;
  const objects = asObj(root.nonresidential_objects);
  const count = us24Value(root.nonresidential_objects, supported);
  const kindsCount = num(objects.object_type_count);
  return {
    period: us24Period(asObj(root.volume).period),
    volume: us24Metric(root.volume, root.volume_ifo, supported),
    housing: us24Metric(root.housing_area, root.housing_area_ifo, supported),
    apartments: us24Value(root.apartments, supported),
    nonresidential:
      count === null && kindsCount === null
        ? null
        : { count, kinds_count: kindsCount },
  };
};

/**
 * Величина блока «Ввод объектов нежилого назначения»: строка, закрытая в
 * источнике «х», показывается как «нет данных», а не «конфиденциально»
 * (DP-518) — числа по виду объекта у пользователя нет в обоих случаях.
 * Бэк состояние `confidential` по-прежнему отдаёт (US-24 FR-5.3); сводится
 * оно только в этом блоке и только на экране.
 */
const us24NonresValue = (raw: unknown): StatValue | null => {
  const value = us24Value(raw);
  return value?.state === 'confidential'
    ? { ...value, state: 'no_data' }
    : value;
};

const us24Territory = (raw: unknown): ConstructionNonresidentialTerritory => {
  const row = asObj(raw);
  return {
    kato: numLoose(row.kato),
    name: us24Text(row, 'name'),
    count: us24NonresValue(row.objects_count),
    actual: us24NonresValue(row.capacity),
  };
};

export const normalizeConstructionNonresidentialUs24 = (
  raw: unknown,
): ConstructionNonresidentialResponse => {
  const root = asObj(raw);
  const empty = root.has_data === false || root.level_supported === false;
  // Порядок строк задаёт ответ — экран не сортирует (US-24 FR-7.2).
  const rows = empty
    ? []
    : asArr(root.rows).flatMap<ConstructionNonresidentialRow>((item) => {
        const row = asObj(item);
        const name = us24Text(row, 'name');
        if (name === null) return [];
        return [
          {
            code: row.object_type_code == null ? null : String(row.object_type_code),
            name,
            full_name: null,
            count: us24NonresValue(row.objects_count),
            actual: us24NonresValue(row.capacity),
            territories: [],
          },
        ];
      });
  const first = asObj(asArr(root.rows)[0]);
  return {
    period: us24Period(root.period),
    rows,
    source: us24Source(asObj(first.objects_count).source),
  };
};

const us24Emergency = (
  raw: unknown,
  levelSupported: boolean,
): ConstructionEmergencyMetric | null => {
  const value = us24Value(raw, levelSupported);
  return value === null ? null : { value, change: null };
};

export const normalizeConstructionEmergencyUs24 = (
  raw: unknown,
): ConstructionEmergencyHousingResponse => {
  const root = asObj(raw);
  const supported = root.level_supported !== false;
  // Без данных блок показывает «нет данных», а не три пустые плитки.
  const empty = root.has_data === false;
  return {
    period: us24Period(root.period),
    area: empty ? null : us24Emergency(root.area, supported),
    houses: empty ? null : us24Emergency(root.houses, supported),
    residents: empty ? null : us24Emergency(root.residents, supported),
    source: us24Source(asObj(root.area).source),
  };
};

// --- /analytics/social (US-20 «Социальная сфера») ---
//
// Операция одна на весь подраздел: раздельных `social/plashka`,
// `social/education`, `social/healthcare` (предложение US-21 FR-1.1) на
// стендах нет — они отвечают 404, тогда как `/public/analytics/social`
// существует. Разбор приводит единый ответ к трём прежним моделям, и
// состав ответа при этом не домысливается: чего в нём нет, то остаётся
// величиной без данных, а плитка показывает «нет данных».
//
// Ответ разбирается терпимо к обеим формам контракта, потому что он
// менялся вместе с составом виджетов:
//  - величины плашки лежат в `plashka` либо в корне; числом (состав
//    US-04.4) либо объектом-величиной (US-21) — `statValue` понимает оба;
//  - `education` / `healthcare` — либо массив строк, либо объект
//    `{ period, rows, source }`;
//  - наименование строки — `name` либо `type`, количество — `count`
//    либо `schools`, код — `code` либо `category`.

/** Период блока: реквизиты ответа, иначе отчётный год всего ответа. */
const socialPeriod = (raw: unknown, year: number | null): StatPeriod | null => {
  const period = statPeriod(raw);
  if (period !== null) return period;
  return year === null
    ? null
    : { type: 'year', label: String(year), lagging: false };
};

/** Блок категорий: массив строк или объект `{ period, rows, source }`. */
const socialRowsBlock = (
  raw: unknown,
): { period: unknown; rows: unknown[]; source: unknown } =>
  Array.isArray(raw)
    ? { period: null, rows: raw, source: null }
    : (() => {
        const obj = asObj(raw);
        return { period: obj.period, rows: asArr(obj.rows), source: obj.source };
      })();

export const normalizeSocialPlashka = (raw: unknown): SocialPlashkaResponse => {
  const root = asObj(raw);
  // Величина ищется в `plashka`, а если её там нет — в корне ответа.
  const plashka = asObj(root.plashka);
  const pick = (key: string): unknown => plashka[key] ?? root[key];
  const rawWage = pick('wage_index');
  const wage = asObj(rawWage);
  const wageValue = statValue(wage.value ?? rawWage);
  const wagePeriod = statPeriod(wage.period);
  const baseLabel = str(wage.base_label);

  return {
    period: socialPeriod(root.period, num(root.year)),
    employed: statValue(pick('employed')),
    unemployed: statValue(pick('unemployed')),
    // Уровень безработицы — готовое значение источника; из занятых и
    // безработных он не выводится (US-21 FR-3.3).
    unemployment_rate: statValue(pick('unemployment_rate')),
    wage_index:
      wageValue === null && wagePeriod === null && baseLabel === null
        ? null
        : { period: wagePeriod, value: wageValue, base_label: baseLabel },
    asp_recipients: statValue(pick('asp_recipients')),
    source: statSource(root.source),
  };
};

const socialDetails = (raw: unknown): SocialCategoryDetail[] =>
  asArr(raw).map((item) => {
    const detail = asObj(item);
    return {
      name: str(detail.name),
      count: statValue(detail.count),
      people: statValue(detail.people),
    };
  });

export const normalizeSocialEducation = (
  raw: unknown,
  year: number | null = null,
): SocialEducationResponse => {
  const block = socialRowsBlock(raw);

  return {
    period: socialPeriod(block.period, year),
    // Порядок категорий задаёт ответ (US-21 FR-7.1); строка без
    // наименования уровня образования показать нечего.
    rows: block.rows.flatMap<SocialEducationRow>((item) => {
      const row = asObj(item);
      const name = str(row.name) ?? str(row.type);
      if (name === null) return [];
      return [
        {
          code: str(row.code) ?? str(row.category),
          name,
          count: statValue(row.count ?? row.schools),
          students: statValue(row.students),
          students_label: str(row.students_label),
          details: socialDetails(row.details),
        },
      ];
    }),
    source: statSource(block.source),
  };
};

export const normalizeSocialHealthcare = (
  raw: unknown,
  year: number | null = null,
): SocialHealthcareResponse => {
  const block = socialRowsBlock(raw);

  return {
    period: socialPeriod(block.period, year),
    rows: block.rows.flatMap<SocialHealthcareRow>((item) => {
      const row = asObj(item);
      const name = str(row.name) ?? str(row.type);
      if (name === null) return [];
      return [
        {
          code: str(row.code) ?? str(row.category),
          name,
          count: statValue(row.count),
          details: socialDetails(row.details),
        },
      ];
    }),
    source: statSource(block.source),
  };
};

/** Весь подраздел одним ответом — то, что реально отдаёт бэк. */
export const normalizeSocialAnalytics = (
  raw: unknown,
): SocialAnalyticsResponse => {
  const root = asObj(raw);
  const year = num(root.year);

  return {
    year,
    plashka: normalizeSocialPlashka(root),
    education: normalizeSocialEducation(root.education, year),
    healthcare: normalizeSocialHealthcare(root.healthcare, year),
  };
};

// --- /analytics/agriculture/* (US-17 «Сельское хозяйство», контракт — US-18) ---
// Шесть операций разбираются «как приходят» (см. `AgricultureMeasure` и
// соседей в types): пара ru/kz нужна на рендере, потому что язык
// переключается без перезапроса, а единица объявлена у каждой строки.
// Контракт — `digital-passport-v2/docs/US-18_agriculture_api.md` (DP-427).

/**
 * Шапка, общая для шести операций. Умолчания — самые безопасные: блок
 * считается включённым и уровень обслуживаемым (иначе рассинхрон каталога
 * или старый ответ молча спрятали бы данные), а `has_data` — false, пока
 * ответ не сказал обратного.
 */
const agricultureHead = (root: Record<string, unknown>): AgricultureBlockHead => ({
  kato: num(root.kato),
  kato_level: str(root.kato_level),
  module_id: str(root.module_id),
  widget_enabled: root.widget_enabled !== false,
  level_supported: root.level_supported !== false,
  has_data: bool(root.has_data),
  report_type: str(root.report_type),
});

/** Величина `MeasureOut`: значение + своя единица + охват (US-18 FR-3.2). */
const agricultureMeasure = (raw: unknown): AgricultureMeasure | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const value = num(obj.value);
  return {
    key: str(obj.key),
    name_ru: str(obj.name_ru),
    name_kz: str(obj.name_kz),
    value,
    // `no_data` читается из ответа, а не выводится из `value === null`:
    // ноль и «нет данных» — разные вещи (US-18 FR-4.1).
    no_data: obj.no_data === undefined ? value === null : bool(obj.no_data),
    unit: str(obj.unit),
    unit_ru: str(obj.unit_ru),
    unit_kz: str(obj.unit_kz),
    coverage: str(obj.coverage),
    coverage_ru: str(obj.coverage_ru),
    coverage_kz: str(obj.coverage_kz),
    cumulative: bool(obj.cumulative),
  };
};

const agricultureMeasures = (raw: unknown): AgricultureMeasure[] =>
  asArr(raw).flatMap<AgricultureMeasure>((item) => {
    const measure = agricultureMeasure(item);
    return measure === null ? [] : [measure];
  });

const agricultureIfo = (raw: unknown): AgricultureIfo | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const value = num(obj.value);
  return {
    value,
    no_data: obj.no_data === undefined ? value === null : bool(obj.no_data),
    unit_ru: str(obj.unit_ru),
    unit_kz: str(obj.unit_kz),
    // База сравнения — готовая подпись ответа (US-18 FR-2.5): вычитание
    // года из подписи периода на четырнадцать регионов дало бы
    // четырнадцать написаний одного и того же.
    base_ru: str(obj.base_ru),
    base_kz: str(obj.base_kz),
  };
};

const agriculturePeriod = (raw: unknown): AgriculturePeriod | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  return {
    year: num(obj.year),
    month: num(obj.month),
    label_ru: str(obj.label_ru),
    label_kz: str(obj.label_kz),
    date_to: str(obj.date_to),
    fallback_from_requested: bool(obj.fallback_from_requested),
  };
};

const agricultureRelease = (raw: unknown): AgricultureRelease | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  const release: AgricultureRelease = {
    source_file: str(obj.source_file),
    source_sheet: str(obj.source_sheet),
    published_at: str(obj.published_at),
    actual_at: str(obj.actual_at),
  };
  return release.source_file === null &&
    release.source_sheet === null &&
    release.published_at === null &&
    release.actual_at === null
    ? null
    : release;
};

const agricultureGrossRows = (raw: unknown): AgricultureGrossRow[] =>
  asArr(raw).flatMap<AgricultureGrossRow>((item) => {
    const row = asObj(item);
    const cut = str(row.cut);
    if (cut === null) return [];
    return [
      {
        cut,
        name_ru: str(row.name_ru),
        name_kz: str(row.name_kz),
        // Пара «объём + ИФО» приходит по одному и тому же разрезу (US-18
        // FR-5.3) — экран её ни по номеру строки, ни делением не собирает.
        volume: agricultureMeasure(row.volume),
        ifo: agricultureIfo(row.ifo),
      },
    ];
  });

export const normalizeAgricultureSummary = (
  raw: unknown,
): AgricultureSummaryResponse => {
  const root = asObj(raw);
  return {
    ...agricultureHead(root),
    period: agriculturePeriod(root.period),
    rows: agricultureGrossRows(root.rows),
    additional_cuts: agricultureGrossRows(root.additional_cuts),
    release: agricultureRelease(root.release),
  };
};

export const normalizeAgricultureProducers = (
  raw: unknown,
): AgricultureProducersResponse => {
  const root = asObj(raw);
  return {
    ...agricultureHead(root),
    // Показатель «на дату»: периода накопления в ответе нет вовсе, иначе
    // экран подписал бы производителей «январь–июль» (US-18 FR-2.2).
    state_date: str(root.state_date),
    set_kind: str(root.set_kind),
    set_kind_ru: str(root.set_kind_ru),
    set_kind_kz: str(root.set_kind_kz),
    categories: agricultureMeasures(root.categories),
    // Итог — готовый из ответа (US-18 FR-3.6): сложение строк на экране
    // при пропуске одной категории дало бы заниженный итог без признака.
    total: agricultureMeasure(root.total),
    // Домашние хозяйства населения — вне итога (US-18 FR-6.3).
    outside_total: agricultureMeasures(root.outside_total),
    release: agricultureRelease(root.release),
  };
};

export const normalizeAgricultureLivestockProduction = (
  raw: unknown,
): AgricultureLivestockProductionResponse => {
  const root = asObj(raw);
  // Итог блока не читается даже при появлении поля в ответе: тонны и
  // тыс. штук не суммируются (US-17 FR-4.4, US-18 FR-7.3).
  return {
    ...agricultureHead(root),
    period: agriculturePeriod(root.period),
    series: str(root.series),
    rows: agricultureMeasures(root.rows),
    release: agricultureRelease(root.release),
  };
};

export const normalizeAgricultureHeadcount = (
  raw: unknown,
): AgricultureHeadcountResponse => {
  const root = asObj(raw);
  const total = agricultureMeasure(root.total);
  return {
    ...agricultureHead(root),
    state_date: str(root.state_date),
    series: str(root.series),
    rows: agricultureMeasures(root.rows),
    total:
      total === null
        ? null
        : // `complete=false` ⇒ `value=null`: неполная сумма неотличима от
          // настоящей, поэтому сервер её не отдаёт (US-18 FR-3.6).
          { ...total, complete: asObj(root.total).complete !== false },
    release: agricultureRelease(root.release),
  };
};

export const normalizeAgricultureCropArea = (
  raw: unknown,
): AgricultureCropAreaResponse => {
  const root = asObj(raw);
  return {
    ...agricultureHead(root),
    harvest_year: num(root.harvest_year),
    // Предварительность — свойство выпуска (US-18 FR-10.2), подписывается
    // на экране; отсутствие поля трактуем как «предварительно», как бэк.
    is_preliminary: root.is_preliminary !== false,
    rows: asArr(root.rows).flatMap<AgricultureSownAreaRow>((item) => {
      const row = asObj(item);
      const culture = str(row.culture);
      if (culture === null) return [];
      return [
        {
          culture,
          name_ru: str(row.name_ru),
          name_kz: str(row.name_kz),
          total: agricultureMeasure(row.total),
          // Разрезы по категориям хозяйств — составные части «всего»
          // (US-18 FR-10.2.1): на экран не идут и сложению не подлежат.
          parts: asArr(row.parts).flatMap<AgricultureSownAreaPart>((part) => {
            const measure = agricultureMeasure(part);
            return measure === null
              ? []
              : [{ ...measure, part_of: str(asObj(part).part_of) }];
          }),
        },
      ];
    }),
    release: agricultureRelease(root.release),
  };
};

export const normalizeAgricultureCropYield = (
  raw: unknown,
): AgricultureCropYieldResponse => {
  const root = asObj(raw);
  return {
    ...agricultureHead(root),
    harvest_year: num(root.harvest_year),
    series: str(root.series),
    // Пара «сбор + урожайность» соответствует одной культуре (US-18
    // FR-10.4) — экран их по номерам строк источника не сопоставляет.
    rows: asArr(root.rows).flatMap<AgricultureCropYieldRow>((item) => {
      const row = asObj(item);
      const culture = str(row.culture);
      if (culture === null) return [];
      return [
        {
          culture,
          name_ru: str(row.name_ru),
          name_kz: str(row.name_kz),
          gross_harvest: agricultureMeasure(row.gross_harvest),
          crop_yield: agricultureMeasure(row.crop_yield),
        },
      ];
    }),
    release: agricultureRelease(root.release),
  };
};

// --- «Экономика», первая волна: два новых блока (US-36.1 / DP-478) ---
// Формы ответов повторяют контракт один в один: экран не додумывает ни
// период, ни единицу, ни базу индекса, ни уровень публикации.

const economyPeriodFrom = (raw: unknown): EconomyPeriod | null => {
  const e = asObj(raw);
  const reportType = str(e.report_type);
  if (reportType === null) return null;
  return {
    report_type: reportType,
    label_ru: str(e.label_ru),
    label_kz: str(e.label_kz),
    year: num(e.year),
    month: num(e.month),
    quarter: num(e.quarter),
    is_lagging: e.is_lagging === true,
  };
};

const economySourceFrom = (raw: unknown): EconomySource | null => {
  const e = asObj(raw);
  const code = str(e.code);
  if (code === null) return null;
  return {
    code,
    name_ru: str(e.name_ru),
    name_kz: str(e.name_kz),
    actual_at: str(e.actual_at),
  };
};

const economyValueFrom = (entry: unknown): EconomyValue[] => {
  const e = asObj(entry);
  const key = str(e.key);
  if (key === null) return [];
  return [
    {
      key,
      part: str(e.part) ?? 'TOTAL',
      measure: str(e.measure) ?? '',
      name_ru: str(e.name_ru),
      name_kz: str(e.name_kz),
      value: num(e.value),
      // Неизвестное состояние безопаснее считать отсутствием данных, чем
      // значением: нуля на экране быть не должно (FR-6.1).
      state: str(e.state) ?? 'NO_DATA',
      unit: str(e.unit),
      unit_ru: str(e.unit_ru),
      unit_kz: str(e.unit_kz),
      origin: str(e.origin) ?? 'PUBLISHED',
      rule_ru: str(e.rule_ru),
      rule_kz: str(e.rule_kz),
      base_ru: str(e.base_ru),
      base_kz: str(e.base_kz),
      index_kind: str(e.index_kind),
    },
  ];
};

const economyIndicatorFrom = (entry: unknown): EconomyIndicator[] => {
  const e = asObj(entry);
  const key = str(e.key);
  if (key === null) return [];
  return [
    {
      key,
      order: num(e.order) ?? 0,
      name_ru: str(e.name_ru),
      name_kz: str(e.name_kz),
      direction: str(e.direction) ?? 'HIGHER_IS_BETTER',
      publication_levels: asArr(e.publication_levels).flatMap(
        (level) => str(level) ?? [],
      ),
      value_level: str(e.value_level),
      value_kato: num(e.value_kato),
      // Умолчание — «публикуется»: отсутствие поля не должно читаться как
      // признак чужого уровня.
      level_published: e.level_published !== false,
      has_data: e.has_data === true,
      period: economyPeriodFrom(e.period),
      source: economySourceFrom(e.source),
      values: asArr(e.values).flatMap(economyValueFrom),
    },
  ];
};

const economyHeadFrom = (raw: unknown): EconomyBlockHead => {
  const root = asObj(raw);
  return {
    kato: num(root.kato),
    kato_level: str(root.kato_level),
    module_id: str(root.module_id),
    widget_enabled: root.widget_enabled !== false,
    level_supported: root.level_supported !== false,
    has_data: root.has_data === true,
  };
};

export const normalizeRegionEconomy = (
  raw: unknown,
): RegionEconomyResponse => ({
  ...economyHeadFrom(raw),
  // Порядок карточек задаёт `order` ответа: места ВРП (1) и МСП (6)
  // зарезервированы второй волной (US-36.1 FR-7.1).
  indicators: asArr(asObj(raw).indicators)
    .flatMap(economyIndicatorFrom)
    .sort((a, b) => a.order - b.order),
});

export const normalizePricesLabor = (raw: unknown): PricesLaborResponse => ({
  ...economyHeadFrom(raw),
  groups: asArr(asObj(raw).groups)
    .flatMap((entry): EconomyGroup[] => {
      const e = asObj(entry);
      const key = str(e.key);
      if (key === null) return [];
      return [
        {
          key,
          order: num(e.order) ?? 0,
          name_ru: str(e.name_ru),
          name_kz: str(e.name_kz),
          indicators: asArr(e.indicators)
            .flatMap(economyIndicatorFrom)
            .sort((a, b) => a.order - b.order),
        },
      ];
    })
    .sort((a, b) => a.order - b.order),
});

// --- запросы ---

const request = async <T>(
  path: string,
  normalize: (raw: unknown) => T,
  kato: number,
  year?: number | null,
): Promise<T> => {
  const params: Record<string, number> = { kato };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>(path, { params });
  return normalize(response.data);
};

export const getAnalyticsAll = (
  kato: number,
  year?: number | null,
): Promise<AnalyticsAllResponse> =>
  request('/public/analytics/all', normalizeAnalyticsAll, kato, year);

export const getAnalyticsPopulation = (
  kato: number,
  year?: number | null,
): Promise<PopulationAnalyticsResponse> =>
  request('/public/analytics/population', normalizePopulationAnalytics, kato, year);

export const getAnalyticsEconomy = (
  kato: number,
  year?: number | null,
): Promise<EconomyAnalyticsResponse> =>
  request('/public/analytics/economy', normalizeEconomyAnalytics, kato, year);

/**
 * Страница «Социальная сфера» (US-20) — одна операция на весь подраздел.
 * Разбиение на три (плашка / образование / здравоохранение) было
 * предложением US-21 FR-1.1 и бэком не реализовано: на стендах
 * `/public/analytics/social/plashka|education|healthcare` отвечают 404,
 * существует только `/public/analytics/social`. Плашка и блоки поэтому
 * приходят и обновляются вместе, одним запросом.
 */
/**
 * Блок «Экономика региона» (US-35.1 FR-4). Без периода каждая отрасль
 * отдаёт свой последний доступный: публикации выходят в разные дни, и
 * общая подпись подписала бы часть карточек чужой датой (US-36.1 FR-3.2).
 */
export const getRegionEconomy = (
  kato: number,
  year?: number | null,
): Promise<RegionEconomyResponse> =>
  request(
    '/public/analytics/economy/region-economy',
    normalizeRegionEconomy,
    kato,
    year,
  );

/** Блок «Цены, труд и доходы населения»: инфляция и рынок труда (FR-5, FR-6). */
export const getPricesLabor = (
  kato: number,
  year?: number | null,
): Promise<PricesLaborResponse> =>
  request(
    '/public/analytics/economy/prices-labor',
    normalizePricesLabor,
    kato,
    year,
  );

export const getAnalyticsSocial = (
  kato: number,
  year?: number | null,
): Promise<SocialAnalyticsResponse> =>
  request('/public/analytics/social', normalizeSocialAnalytics, kato, year);

/**
 * Операция прежнего состава подраздела «Сельское хозяйство»: после
 * US-17 её ответ питает только два сохранённых виджета — «Структура
 * посевных площадей» и «Животноводство и птицеводство», которые
 * наполняются ручным вводом (US-17 FR-12.5, US-18 FR-11.1.1).
 */
export const getAnalyticsAgriculture = (
  kato: number,
  year?: number | null,
): Promise<AgricultureAnalyticsResponse> =>
  request('/public/analytics/agriculture', normalizeAgricultureAnalytics, kato, year);

/**
 * Страница «Сельское хозяйство» (US-17): у сводного модуля и каждого из
 * пяти блоков своя операция (US-18 FR-1.1) — валовый выпуск и
 * производство животноводства публикуются ежемесячно, поголовье
 * ежемесячно на дату, производители раз в полгода, посевная площадь раз
 * в год, валовой сбор по итогам уборки, и недоступность одного выпуска
 * не должна обнулять страницу. Прежняя плашка US-04.5 и виджеты мяса и
 * молочной продукции сняты вместе с прежним составом (US-17 FR-1.1).
 *
 * Контракт — `digital-passport-v2/docs/US-18_agriculture_api.md` (задача
 * бэка DP-427, ветка `stage`).
 *
 * **Период в параметрах.** Две периодные операции принимают `year` ТОЛЬКО
 * вместе с `month`, а месяца на экране не выбирают — поэтому год им не
 * передаётся и отдаётся последний доступный период (год без месяца бэк
 * всё равно игнорирует, а `month=12` спросил бы декабрь и вернул чужой
 * период с признаком подмены). Две операции «на дату» фильтруются
 * `state_date`, которой на экране тоже нет. Год принимают только два
 * годовых блока — и у них он значит разное: год посева против года уборки
 * (US-18 FR-10.5).
 */
const agriculture = async <T>(
  path: string,
  normalize: (raw: unknown) => T,
  kato: number,
  year?: number | null,
): Promise<T> => {
  const params: Record<string, number> = { kato };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>(
    `/public/analytics/agriculture/${path}`,
    { params },
  );
  return normalize(response.data);
};

export const getAnalyticsAgricultureSummary = (
  kato: number,
): Promise<AgricultureSummaryResponse> =>
  agriculture('summary', normalizeAgricultureSummary, kato);

export const getAnalyticsAgricultureProducers = (
  kato: number,
): Promise<AgricultureProducersResponse> =>
  agriculture('producers', normalizeAgricultureProducers, kato);

export const getAnalyticsAgricultureProduction = (
  kato: number,
): Promise<AgricultureLivestockProductionResponse> =>
  agriculture(
    'livestock-production',
    normalizeAgricultureLivestockProduction,
    kato,
  );

export const getAnalyticsAgricultureHeadcount = (
  kato: number,
): Promise<AgricultureHeadcountResponse> =>
  agriculture('livestock-headcount', normalizeAgricultureHeadcount, kato);

export const getAnalyticsAgricultureCropArea = (
  kato: number,
  year?: number | null,
): Promise<AgricultureCropAreaResponse> =>
  agriculture('crop-area', normalizeAgricultureCropArea, kato, year);

export const getAnalyticsAgricultureCropYield = (
  kato: number,
  year?: number | null,
): Promise<AgricultureCropYieldResponse> =>
  agriculture('crop-yield', normalizeAgricultureCropYield, kato, year);

export const getAnalyticsLandFund = (
  kato: number,
  year?: number | null,
): Promise<LandFundAnalyticsResponse> =>
  request('/public/analytics/land-fund', normalizeLandFundAnalytics, kato, year);

export const getAnalyticsInfrastructure = (
  kato: number,
  year?: number | null,
): Promise<InfrastructureAnalyticsResponse> =>
  request('/public/analytics/infrastructure', normalizeInfrastructureAnalytics, kato, year);

/**
 * Страница «Строительство» (US-23) — три операции US-24 плюс разрез по
 * территориям внутри вида объекта. Операции грузятся независимо:
 * недоступность квартального выпуска (503) не обнуляет плашку, и
 * наоборот. Разрез запрашивается по каждому виду; не пришёл — строка
 * просто не раскрывается. Части, которых нет на экране (`parts`), не
 * запрашиваются.
 */
export const getAnalyticsConstruction = async (
  kato: number,
  year?: number | null,
  parts: { summary: boolean; nonresidential: boolean; emergency: boolean } = {
    summary: true,
    nonresidential: true,
    emergency: true,
  },
): Promise<ConstructionAnalyticsResponse> => {
  const params: Record<string, number> = { kato };
  if (year != null) params.year = year;
  const base = '/public/analytics/construction';
  const settled = <T>(
    promise: Promise<{ data: unknown }> | null,
    normalize: (raw: unknown) => T,
    fallback: T,
  ): Promise<T> =>
    promise === null
      ? Promise.resolve(fallback)
      : promise.then(
      (response) => normalize(response.data),
      (err: unknown) => {
        console.error('construction request failed', err);
        return fallback;
      },
    );

  const [plashka, nonresidential, emergency] = await Promise.all([
    settled(
      parts.summary ? api.get<unknown>(`${base}/summary`, { params }) : null,
      normalizeConstructionSummary,
      normalizeConstructionSummary({}),
    ),
    settled(
      parts.nonresidential
        ? api.get<unknown>(`${base}/nonresidential`, { params })
        : null,
      normalizeConstructionNonresidentialUs24,
      normalizeConstructionNonresidentialUs24({}),
    ),
    settled(
      parts.emergency
        ? api.get<unknown>(`${base}/emergency-housing`, { params })
        : null,
      normalizeConstructionEmergencyUs24,
      normalizeConstructionEmergencyUs24({ has_data: false }),
    ),
  ]);

  const rows = await Promise.all(
    nonresidential.rows.map((row) =>
      row.code === null
        ? Promise.resolve(row)
        : settled(
            api.get<unknown>(`${base}/nonresidential/territories`, {
              params: { ...params, object_type_code: row.code },
            }),
            (raw) => ({
              ...row,
              territories: asArr(asObj(raw).territories).map(us24Territory),
            }),
            row,
          ),
    ),
  );

  return {
    year: year ?? null,
    plashka,
    nonresidential: { ...nonresidential, rows },
    emergency_housing: emergency,
  };
};

export const getAnalyticsSnpNeeds = (
  kato: number,
  year?: number | null,
): Promise<SnpNeedsData> =>
  request('/public/analytics/snp-needs', normalizeSnpNeeds, kato, year);

/**
 * Страница «Торговля» (US-30): у плашки и каждого блока своя операция —
 * периодичность источников разная, и недоступность годового выпуска
 * структуры не должна обнулять месячную плашку (US-31 FR-1.1).
 * Имена операций — предложение US-31, предмет согласования с бэком.
 */
export const getAnalyticsTradePlashka = (
  kato: number,
  year?: number | null,
): Promise<TradePlashkaResponse> =>
  request('/public/analytics/trade/plashka', normalizeTradePlashka, kato, year);

export const getAnalyticsTradePrices = (
  kato: number,
  year?: number | null,
): Promise<TradePricesResponse> =>
  request('/public/analytics/trade/prices', normalizeTradePrices, kato, year);

export const getAnalyticsTradeWholesale = (
  kato: number,
  year?: number | null,
): Promise<TradeWholesaleResponse> =>
  request(
    '/public/analytics/trade/wholesale-structure',
    normalizeTradeWholesale,
    kato,
    year,
  );

export const getAnalyticsTradeTerritories = (
  kato: number,
  year?: number | null,
): Promise<TradeTerritoriesResponse> =>
  request(
    '/public/analytics/trade/territories',
    normalizeTradeTerritories,
    kato,
    year,
  );
